/**
 * Country utilities for displaying flags and names
 *
 * Cloudflare provides ISO 3166-1 alpha-2 country codes (e.g., "US", "AM", "DE")
 * These utilities convert them to display-friendly formats.
 */

/**
 * Convert ISO 3166-1 alpha-2 country code to flag emoji
 *
 * Works by converting each letter to its "Regional Indicator Symbol" Unicode equivalent.
 * Example: "US" -> "🇺🇸", "AM" -> "🇦🇲"
 *
 * @param code - Two-letter country code (e.g., "US", "GB", "AM")
 * @returns Flag emoji or empty string if invalid
 */
export function countryCodeToFlag(code: string): string {
  if (!code || code.length !== 2) return '';

  // Regional Indicator Symbol Letters start at U+1F1E6 (🇦)
  // Each letter A-Z maps to its corresponding regional indicator
  const OFFSET = 0x1f1e6 - 65; // 65 is 'A'.charCodeAt(0)

  const upper = code.toUpperCase();
  const first = upper.charCodeAt(0);
  const second = upper.charCodeAt(1);

  // Validate it's A-Z
  if (first < 65 || first > 90 || second < 65 || second > 90) {
    return '';
  }

  return String.fromCodePoint(first + OFFSET) + String.fromCodePoint(second + OFFSET);
}

/**
 * Map of ISO 3166-1 alpha-2 codes to country names
 *
 * Only includes commonly seen countries in web analytics.
 * Unknown codes return the code itself as fallback.
 */
const COUNTRY_NAMES: Record<string, string> = {
  // North America
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',

  // Europe
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  IT: 'Italy',
  ES: 'Spain',
  NL: 'Netherlands',
  BE: 'Belgium',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  AT: 'Austria',
  CH: 'Switzerland',
  PT: 'Portugal',
  IE: 'Ireland',
  GR: 'Greece',
  CZ: 'Czech Republic',
  RO: 'Romania',
  HU: 'Hungary',
  UA: 'Ukraine',
  RU: 'Russia',

  // Asia
  CN: 'China',
  JP: 'Japan',
  KR: 'South Korea',
  IN: 'India',
  TH: 'Thailand',
  VN: 'Vietnam',
  ID: 'Indonesia',
  MY: 'Malaysia',
  SG: 'Singapore',
  PH: 'Philippines',
  TW: 'Taiwan',
  HK: 'Hong Kong',
  PK: 'Pakistan',
  BD: 'Bangladesh',

  // Middle East
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  IL: 'Israel',
  TR: 'Turkey',
  EG: 'Egypt',
  IR: 'Iran',

  // Caucasus & Central Asia
  AM: 'Armenia',
  GE: 'Georgia',
  AZ: 'Azerbaijan',
  KZ: 'Kazakhstan',

  // South America
  BR: 'Brazil',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  VE: 'Venezuela',

  // Oceania
  AU: 'Australia',
  NZ: 'New Zealand',

  // Africa
  ZA: 'South Africa',
  NG: 'Nigeria',
  KE: 'Kenya',
  MA: 'Morocco',

  // Caribbean
  JM: 'Jamaica',
  PR: 'Puerto Rico',
  CU: 'Cuba',
};

/**
 * Get full country name from ISO 3166-1 alpha-2 code
 *
 * @param code - Two-letter country code
 * @returns Full country name or the code itself if unknown
 */
export function getCountryName(code: string): string {
  if (!code) return 'Unknown';
  const upper = code.toUpperCase();
  return COUNTRY_NAMES[upper] || upper;
}

/**
 * Get both flag and name for display
 *
 * @param code - Two-letter country code
 * @returns Object with flag emoji and country name
 */
export function getCountryDisplay(code: string): { flag: string; name: string } {
  return {
    flag: countryCodeToFlag(code),
    name: getCountryName(code),
  };
}

/**
 * Convert ISO alpha-2 to alpha-3 codes for react-simple-maps TopoJSON
 *
 * TopoJSON typically uses alpha-3 codes (e.g., "USA" instead of "US")
 */
const ALPHA2_TO_ALPHA3: Record<string, string> = {
  US: 'USA', CA: 'CAN', MX: 'MEX', GB: 'GBR', DE: 'DEU', FR: 'FRA', IT: 'ITA',
  ES: 'ESP', NL: 'NLD', BE: 'BEL', SE: 'SWE', NO: 'NOR', DK: 'DNK', FI: 'FIN',
  PL: 'POL', AT: 'AUT', CH: 'CHE', PT: 'PRT', IE: 'IRL', GR: 'GRC', CZ: 'CZE',
  RO: 'ROU', HU: 'HUN', UA: 'UKR', RU: 'RUS', CN: 'CHN', JP: 'JPN', KR: 'KOR',
  IN: 'IND', TH: 'THA', VN: 'VNM', ID: 'IDN', MY: 'MYS', SG: 'SGP', PH: 'PHL',
  TW: 'TWN', HK: 'HKG', PK: 'PAK', BD: 'BGD', AE: 'ARE', SA: 'SAU', IL: 'ISR',
  TR: 'TUR', EG: 'EGY', IR: 'IRN', AM: 'ARM', GE: 'GEO', AZ: 'AZE', KZ: 'KAZ',
  BR: 'BRA', AR: 'ARG', CL: 'CHL', CO: 'COL', PE: 'PER', VE: 'VEN', AU: 'AUS',
  NZ: 'NZL', ZA: 'ZAF', NG: 'NGA', KE: 'KEN', MA: 'MAR', JM: 'JAM', PR: 'PRI',
  CU: 'CUB',
};

/**
 * Convert ISO alpha-2 code to alpha-3 code
 *
 * @param alpha2 - Two-letter country code
 * @returns Three-letter country code or original if not found
 */
export function toAlpha3(alpha2: string): string {
  if (!alpha2) return '';
  return ALPHA2_TO_ALPHA3[alpha2.toUpperCase()] || alpha2.toUpperCase();
}

/**
 * Convert ISO alpha-3 code to alpha-2 code
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = Object.fromEntries(
  Object.entries(ALPHA2_TO_ALPHA3).map(([k, v]) => [v, k])
);

export function toAlpha2(alpha3: string): string {
  if (!alpha3) return '';
  return ALPHA3_TO_ALPHA2[alpha3.toUpperCase()] || alpha3.toUpperCase();
}
